import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "src/components/ui/tooltip";
import pluralize from "pluralize";
import { FC, ReactNode } from "react";
import { Plus, X, CheckCircle2 } from "lucide-react";
import MedalIcon from "src/assets/icon/medal.svg?react";
import { Button } from "src/components/ui/button";

interface Props {
	completed?: boolean;
	graded?: boolean;
	children?: ReactNode;
	totalNumObservations?: number;
	showIcon?: boolean;
	onClick?: () => void;
	observationType?: IObservationType;
}

const ViewObservationFormTableCellButton: FC<Props> = ({
	completed = false,
	graded = false,
	showIcon = false,
	totalNumObservations,
	children,
	onClick,
	observationType
}) => {
	const isNotGraded = observationType === "GREAT_CONVERSATION" && !graded;

	const renderIcon = () => {
		if (observationType === "GREAT_CONVERSATION" && graded) {
			return <MedalIcon data-testid="medal-icon" />;
		}

		if (observationType === "GREAT_CONVERSATION" && !graded) {
			return <Plus />;
		}

		if (completed) {
			return <CheckCircle2 className="text-white" size={18} />;
		}

		return <X data-testid="md-close" className="text-white" size={18} />;
	};

	const label = isNotGraded
		? "Grade Employee"
		: `View ${pluralize("form", totalNumObservations)}`;

	const iconBgClass = completed
		? onClick
			? "bg-emerald-500 hover:bg-emerald-600"
			: "bg-emerald-500"
		: onClick
			? "bg-red-400 hover:bg-red-500"
			: "bg-red-400";

	const textBgClass = onClick
		? "bg-emerald-500 hover:bg-emerald-600 cursor-pointer"
		: "bg-emerald-500";

	const textButtonClasses = [
		"min-w-[36px] h-[36px] rounded-lg",
		"inline-flex items-center justify-center",
		"transition-colors duration-150",
		"text-white font-semibold text-sm",
		textBgClass
	]
		.filter(Boolean)
		.join(" ");

	const renderButton = () => {
		if (showIcon && !onClick) {
			return (
				<span
					role="img"
					aria-label={completed ? "Compliant" : "Non compliant"}
					className={`min-w-[36px] h-[36px] rounded-lg inline-flex items-center justify-center ${iconBgClass}`}
				>
					{renderIcon()}
				</span>
			);
		}

		if (showIcon) {
			return (
				<button
					type="button"
					onClick={onClick}
					aria-label={label}
					className={`min-w-[36px] h-[36px] rounded-lg inline-flex items-center justify-center transition-colors duration-150 cursor-pointer ${iconBgClass}`}
				>
					{renderIcon()}
				</button>
			);
		}

		if (!onClick) {
			return (
				<span aria-label={label} className={textButtonClasses}>
					{children}
				</span>
			);
		}

		return (
			<button
				type="button"
				onClick={onClick}
				aria-label={label}
				className={textButtonClasses}
			>
				{children}
			</button>
		);
	};

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>{renderButton()}</TooltipTrigger>
					{onClick && <TooltipContent>{label}</TooltipContent>}
				</Tooltip>
			</TooltipProvider>
		</div>
	);
};

export default ViewObservationFormTableCellButton;
